import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, Calendar, Leaf, ShieldCheck } from 'lucide-react';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '34600000000';

const Footer = () => {
  const { t } = useTranslation();
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`;

  return (
    <footer className="bg-gray-900 text-gray-300 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="md:col-span-2 space-y-4">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src="/logo.png" alt="TODOenBALANCE Logo" className="h-9 w-auto" />
              <span className="text-lg font-extrabold text-white tracking-tight">
                TODOenBALANCE
              </span>
            </Link>
            <p className="text-sm text-gray-400 max-w-md leading-relaxed">
              {t(
                'footer.tagline',
                'Personalized clinical nutrition and mindful eating in English, Español & Italiano.'
              )}
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold px-4 py-2.5 rounded-full transition-colors"
              >
                <Calendar className="w-3.5 h-3.5" />
                {t('navbar.bookNow', 'Book Consultation')}
              </Link>
              <Link
                to="/diets"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-4 py-2.5 rounded-full transition-colors"
              >
                <Leaf className="w-3.5 h-3.5" />
                {t('navbar.diets', 'Diet eBooks')}
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {t('footer.quickLinks', 'Quick Links')}
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/services" className="hover:text-white transition-colors">
                  {t('navbar.services', 'Services')}
                </Link>
              </li>
              <li>
                <Link to="/diets" className="hover:text-white transition-colors">
                  {t('navbar.diets', 'Diet Plans & eBooks')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  {t('navbar.about', 'About Paola')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white transition-colors">
                  {t('navbar.blog', 'Nutrition Blog')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  {t('navbar.contact', 'Contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & Compliance */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              {t('legal.footerHeading', 'Legal & Privacy')}
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/medical-disclaimer" className="hover:text-white transition-colors">
                  {t('legal.medicalDisclaimerLink', 'Medical Disclaimer')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  {t('legal.privacyLink', 'Privacy & Cookie Policy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  {t('legal.termsLink', 'Terms of Sale & Refunds')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Direct Contact */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {t('footer.contactTitle', 'Contact & Hours')}
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="mailto:paola@todoenbalance.com"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors break-all"
                >
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  paola@todoenbalance.com
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@todoenbalance.com"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors break-all"
                >
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  info@todoenbalance.com
                </a>
              </li>
              <li>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
                >
                  <MessageCircle className="w-4 h-4 flex-shrink-0" />
                  WhatsApp Chat
                </a>
              </li>
              <li className="text-xs text-gray-400 pt-1">
                {t('footer.hours', 'Mon–Sat · 09:00–17:00 CET · 100% Online')}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-4">
          <p>
            © {new Date().getFullYear()} TODOenBALANCE · Paola Michelle.{' '}
            {t('footer.rights', 'All rights reserved.')}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/medical-disclaimer" className="hover:text-gray-300 transition-colors">
              {t('legal.medicalDisclaimerLink', 'Medical Disclaimer')}
            </Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-gray-300 transition-colors">
              {t('legal.privacyLink', 'Privacy & Cookie Policy')}
            </Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-gray-300 transition-colors">
              {t('legal.termsLink', 'Terms of Sale & Refunds')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
